/**
 * ECA plugin catalogue API layer.
 *
 * Fetches the plugin catalogue from Drupal's /eca-react-editor/plugins endpoint
 * and adapts the backend format to the internal frontend format.
 *
 * Gateways are NOT fetched from the backend — they are structural ECA constructs
 * (type: 0/1/2), not plugins. They are defined here as editor constants and
 * injected into the catalogue so consumers have a uniform interface.
 */

import { DRUPAL_BASE_URL as BASE } from '../../config/drupal.js';

// ---------------------------------------------------------------------------
// Gateway type constants — mapping with ECA gateway type integers (0/1/2)
// ---------------------------------------------------------------------------

export const GATEWAY_TYPES = [
  {
    id: 'gateway_and',
    ecaType: 0,
    label: 'Wait for all (AND)',
    description: 'Continue quand toutes les branches entrantes ont terminé.',
    configSchema: [],
    tokenOutputs: [],
  },
  {
    id: 'gateway_or',
    ecaType: 1,
    label: 'Any branch (OR)',
    description: "Continue dès qu'une seule branche entrante a terminé.",
    configSchema: [],
    tokenOutputs: [],
  },
  {
    id: 'gateway_xor',
    ecaType: 2,
    label: 'Exclusive (XOR)',
    description: "Continue dès qu'une seule branche termine, ignore les autres.",
    configSchema: [],
    tokenOutputs: [],
  },
];

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

/**
 * Fetches the plugin catalogue from Drupal and returns it in the internal format.
 *
 * @returns {Promise<{events, conditions, actions, gateways}>}
 * @throws  {Error} On non-200 HTTP response.
 */
export const fetchPluginCatalogue = async () => {
  const res = await fetch(`${BASE}/eca-react-editor/plugins`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`ECA catalogue fetch failed: HTTP ${res.status}`);
  }
  return adaptCatalogue(await res.json());
};

// ---------------------------------------------------------------------------
// Adapter — backend format → internal frontend format
// ---------------------------------------------------------------------------

/**
 * Converts the raw backend JSON to the format consumed by picker + node components.
 *
 * Backend configSchema:  { fieldKey: { default, type, label, description, placeholder, wirable, options } }
 * Internal configSchema: [{ key, label, description, type, placeholder, wirable, options, default }]
 *
 * Backend tokenOutputs:  [{ key, tokenNameField? }]
 * Internal tokenOutputs: ['entity', 'account']  (key strings only)
 */
const adaptCatalogue = (raw) => ({
  events:     (raw.events     ?? []).map(adaptPlugin),
  conditions: (raw.conditions ?? []).map(adaptPlugin),
  actions:    (raw.actions    ?? []).map(adaptPlugin),
  // Gateways come from editor constants, not the backend
  gateways:   GATEWAY_TYPES,
});

const adaptPlugin = (p) => ({
  ...p,
  configSchema: Object.entries(p.configSchema ?? {}).map(([key, meta]) => ({
    key,
    label:       meta.label       ?? humanize(key),
    description: meta.description ?? '',
    type:        meta.type        ?? 'text',
    placeholder: meta.placeholder ?? '',
    wirable:     meta.wirable     ?? (meta.type === 'text'),
    tokenRef:    meta.tokenRef    ?? false,
    options:     meta.options     ?? null,
    default:     meta.default,
  })),
  // Normalise tokenOutputs: [{key}] → ['key']
  tokenOutputs: (p.tokenOutputs ?? []).map((t) => (typeof t === 'string' ? t : t.key)),
});

const humanize = (key) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
